/* IFA 2026 首页 —— 锚点导航 / 品牌轮播 / SmartErgo 切换 / 展位互动 + 产品弹窗
 *
 * 纪律（沿用 ifa-product.js 的踩坑结论）：
 *  · 不做「加载时宽度不符就 return」——监听照挂、判定放函数内部，
 *    否则「桌面加载 → 缩窄」这条路径永远是坏的。
 *  · 逐 section 初始化 + 所有 DOM 查询限定在 section 容器内，多实例互不干扰。
 *  · 监听 shopify:section:load，否则主题编辑器里预览是死的。
 *  · 尺寸不写死在 JS 里 —— 展位图宽高、热点坐标全部由 liquid 通过 data-* 注入，
 *    运营换图后不用改代码。
 */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var DESKTOP = '(min-width: 990px)';

  function clamp(v, min, max) {
    return Math.min(max, Math.max(min, v));
  }

  /* ====================================================================
     锚点导航：吸顶 + 滚动高亮当前区块
     ==================================================================== */
  function initNav(root) {
    var links = Array.prototype.slice.call(root.querySelectorAll('[data-ifa-nav-link]'));
    if (!links.length) return;

    var list = root.querySelector('.ifa-home-nav__list');

    function offset() {
      var header = document.querySelector('.ifa-header');
      var headerH = header ? header.getBoundingClientRect().height : 0;
      return headerH + root.getBoundingClientRect().height;
    }

    function scrollActiveLinkIntoView(link) {
      if (!list || !link || window.innerWidth > 990) return;

      var listRect = list.getBoundingClientRect();
      var linkRect = link.getBoundingClientRect();
      var targetLeft = list.scrollLeft + (linkRect.left - listRect.left) - (list.clientWidth - linkRect.width) / 2;
      var maxScrollLeft = Math.max(list.scrollWidth - list.clientWidth, 0);

      list.scrollTo({
        left: clamp(targetLeft, 0, maxScrollLeft),
        behavior: reduceMotion.matches ? 'auto' : 'smooth',
      });
    }

    links.forEach(function (link) {
      link.addEventListener('click', function (e) {
        var id = link.getAttribute('href');
        if (!id || id.charAt(0) !== '#') return;
        var target = document.querySelector(id);
        if (!target) return;
        e.preventDefault();
        var top = target.getBoundingClientRect().top + window.pageYOffset - offset();
        window.scrollTo({
          top: Math.max(top, 0),
          behavior: reduceMotion.matches ? 'auto' : 'smooth',
        });
        scrollActiveLinkIntoView(link);
      });
    });

    var targets = links
      .map(function (l) {
        var id = l.getAttribute('href');
        return id && id.charAt(0) === '#' ? document.querySelector(id) : null;
      })
      .filter(Boolean);
    if (!targets.length || !window.IntersectionObserver) return;

    var visible = {};
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          visible[entry.target.id] = entry.isIntersecting ? entry.intersectionRatio : 0;
        });
        var bestId = null;
        var best = 0;
        Object.keys(visible).forEach(function (id) {
          if (visible[id] > best) {
            best = visible[id];
            bestId = id;
          }
        });

        var activeLink = null;
        links.forEach(function (l) {
          var on = bestId && l.getAttribute('href') === '#' + bestId;
          l.classList.toggle('is-active', !!on);
          if (on) {
            l.setAttribute('aria-current', 'true');
            activeLink = l;
          } else {
            l.removeAttribute('aria-current');
          }
        });

        if (activeLink) {
          scrollActiveLinkIntoView(activeLink);
        }
      },
      // rootMargin 顶部让出吸顶高度，底部收紧，让「当前区块」判定贴近肉眼所见
      { threshold: [0, 0.25, 0.5, 0.75, 1], rootMargin: '-120px 0px -45% 0px' }
    );
    targets.forEach(function (t) {
      io.observe(t);
    });
  }

  /* ====================================================================
     横滑轮播（Our Brands）：与 ifa-product.js 的 More Products 同款
     ==================================================================== */
  function initTrack(root) {
    var track = root.querySelector('[data-ifa-home-track]');
    if (!track) return;

    var prev = root.querySelector('[data-ifa-home-prev]');
    var next = root.querySelector('[data-ifa-home-next]');
    var progress = root.querySelector('[data-ifa-home-progress]');

    function step() {
      var card = track.firstElementChild;
      if (!card) return track.clientWidth;
      var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      return card.getBoundingClientRect().width + gap;
    }

    function sync() {
      var max = track.scrollWidth - track.clientWidth;
      // 容差 1px：不同缩放/像素比下 scrollLeft 会差零点几
      if (prev) prev.disabled = track.scrollLeft <= 1;
      if (next) next.disabled = track.scrollLeft >= max - 1;

      if (progress) {
        var visibleRatio = track.clientWidth / track.scrollWidth;
        var ratio = max > 0 ? track.scrollLeft / max : 0;
        progress.style.width = Math.max(visibleRatio * 100, 12) + '%';
        progress.style.transform =
          'translateX(' + ratio * (100 / Math.max(visibleRatio, 0.12) - 100) + '%)';
      }
    }

    if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: 'smooth' }); });
    if (next) next.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: 'smooth' }); });

    track.addEventListener('scroll', sync, { passive: true });
    if (window.ResizeObserver) new ResizeObserver(sync).observe(track);
    sync();
  }

  /* ====================================================================
     SmartErgo 3×8：激活卡展开（激活卡更宽，其余收窄）
     ==================================================================== */
  function initErgo(root) {
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-ifa-ergo-card]'));
    if (cards.length < 2) return;

    var prev = root.querySelector('[data-ifa-ergo-prev]');
    var next = root.querySelector('[data-ifa-ergo-next]');
    var progress = root.querySelector('[data-ifa-home-progress]');
    var index = 0;

    function show(i) {
      index = clamp(i, 0, cards.length - 1);

      // 2026-08-20：激活卡不再调换位置，只在当前位置放大/收窄，
      // 位移完全由 flex-basis 过渡承担（0.76s，见 ifa-home-smartergo.liquid），
      // 原先为 order 瞬变打补丁的 FLIP 位移动画不再需要。
      cards.forEach(function (c, n) {
        var on = n === index;
        c.classList.toggle('is-active', on);
        c.setAttribute('aria-expanded', on ? 'true' : 'false');
      });

      if (prev) prev.disabled = index === 0;
      if (next) next.disabled = index === cards.length - 1;
      if (progress) {
        progress.style.width = 100 / cards.length + '%';
        progress.style.transform = 'translateX(' + index * 100 + '%)';
      }
      // 手机端是横滑容器，切换后把激活卡滚进视野
      var track = root.querySelector('[data-ifa-ergo-track]');
      if (track && track.scrollWidth > track.clientWidth) {
        track.scrollTo({ left: cards[index].offsetLeft - track.offsetLeft, behavior: 'smooth' });
      }
    }

    // 只认点击切换。稿里没画 hover 态，加 hover 自动切换会让鼠标扫过时布局连续跳动，
    // 也和原型「Work 8h 默认激活」的静态观感对不上（2026-08-10 需求方反馈）。
    cards.forEach(function (card, n) {
      card.addEventListener('click', function () { show(n); });
    });
    if (prev) prev.addEventListener('click', function () { show(index - 1); });
    if (next) next.addEventListener('click', function () { show(index + 1); });

    show(0);          // 初始化默认激活第一张
  }

  /* ====================================================================
     IFA Experience 展位互动 —— 移植自已验证的原型
     /obsidian-kb-brain/…/高难度特效开发/ifa-experience-demo（app.js）

     **只在 <990px 生效**：PC 端按需求方要求保持静态 —— 整张图完整展示，
     不平移不视差，只保留热点点击开抽屉（2026-08-10 需求方确认）。

     移动端还原 demo 的输入：
       · 拖拽   跟手 + rAF 分级缓动
       · 滚轮   触控板横移（移动端罕见，但触控笔记本会用到）
     与 demo 的差异：demo 是独立整页，舞台可以 touch-action:none 霸占所有手势；
     这里嵌在长落地页里，纵向必须留给页面滚动，所以用 pan-y，只接管横向。
     ==================================================================== */
  function initBooth(root) {
    var stage = root.querySelector('[data-ifa-booth-stage]');
    var canvas = root.querySelector('[data-ifa-booth-canvas]');
    if (!stage || !canvas) return;

    var thumb = root.querySelector('[data-ifa-booth-progress]');
    var hint = root.querySelector('[data-ifa-booth-hint]');
    var interacted = false;

    function measure() {
      syncProgress();
    }

    function hideHint() {
      if (interacted || !hint) return;
      interacted = true;
      hint.classList.add('is-hidden');
    }

    function syncProgress() {
      if (!thumb) return;
      var scrollWidth = stage.scrollWidth;
      var viewportWidth = stage.clientWidth;
      var maxScroll = Math.max(scrollWidth - viewportWidth, 0);
      var visibleRatio = scrollWidth > 0 ? Math.min(viewportWidth / scrollWidth, 1) : 1;
      var scrollRatio = maxScroll > 0 ? stage.scrollLeft / maxScroll : 0;
      thumb.style.width = (visibleRatio * 100) + '%';
      thumb.style.transform = 'translateX(' + scrollRatio * (100 / visibleRatio - 100) + '%)';
    }

    stage.addEventListener('scroll', function () {
      syncProgress();
      hideHint();
    }, { passive: true });

    /* --- 热点点击：拖过 8px 就不算点击（demo 验证过的阈值）--- */
    root.querySelectorAll('[data-ifa-hotspot]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();

        var hotspotTitle = btn.getAttribute('data-ifa-hotspot-title') || '';
        if (hotspotTitle) {
          document.dispatchEvent(new CustomEvent('ifa:open-header-menu', {
            detail: { title: hotspotTitle }
          }));
        }

        openDialog(root, btn.getAttribute('data-ifa-hotspot'));
      });
    });

    window.addEventListener('resize', measure, { passive: true });
    window.addEventListener('orientationchange', measure, { passive: true });
    if (window.ResizeObserver) new ResizeObserver(measure).observe(stage);
    measure();
  }

  /* ====================================================================
     产品弹窗（PC 右侧抽屉 / 手机弹层）
     不用 Dawn 的 ModalDialog：它的样式散在 base.css / quick-add.css 里，
     与 .ifa-page 作用域的字体和 box-sizing 要额外协调，得不偿失。
     ==================================================================== */
  var lastFocus = null;

  function openDialog(root, zoneId) {
    var dialog = root.querySelector('[data-ifa-dialog]');
    if (!dialog || !zoneId) return;

    var panels = Array.prototype.slice.call(dialog.querySelectorAll('[data-ifa-zone]'));
    panels.forEach(function (p) {
      p.hidden = p.getAttribute('data-ifa-zone') !== zoneId;
    });
    // 打开时只展开该区域的第一个分类，其余折叠
    var active = dialog.querySelector('[data-ifa-zone="' + zoneId + '"]');
    if (active) {
      active.querySelectorAll('[data-ifa-cat-btn]').forEach(function (btn, n) {
        setCat(btn, n === 0);
      });
    }

    lastFocus = document.activeElement;
    dialog.hidden = false;
    // 先渲染一帧再加 class，否则 transition 不触发（display 与 transform 同帧变化）
    requestAnimationFrame(function () { dialog.classList.add('is-open'); });
    document.documentElement.classList.add('ifa-dialog-open');
    var closeBtn = dialog.querySelector('[data-ifa-dialog-close]');
    if (closeBtn) closeBtn.focus();
  }

  function closeDialog(dialog) {
    if (!dialog || dialog.hidden) return;
    dialog.classList.remove('is-open');
    document.documentElement.classList.remove('ifa-dialog-open');
    var done = function () {
      dialog.hidden = true;
      dialog.removeEventListener('transitionend', done);
    };
    if (reduceMotion.matches) done();
    else dialog.addEventListener('transitionend', done);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function setCat(btn, open) {
    var panel = document.getElementById(btn.getAttribute('aria-controls'));
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (panel) panel.hidden = !open;
  }

  function initDialog(root) {
    var dialog = root.querySelector('[data-ifa-dialog]');
    if (!dialog) return;

    dialog.querySelectorAll('[data-ifa-dialog-close]').forEach(function (btn) {
      btn.addEventListener('click', function () { closeDialog(dialog); });
    });
    var backdrop = dialog.querySelector('[data-ifa-dialog-backdrop]');
    if (backdrop) backdrop.addEventListener('click', function () { closeDialog(dialog); });

    // 分类手风琴：同一时刻只展开一个，与稿一致
    dialog.querySelectorAll('[data-ifa-cat-btn]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') === 'true';
        var zone = btn.closest('[data-ifa-zone]');
        if (zone) zone.querySelectorAll('[data-ifa-cat-btn]').forEach(function (b) { setCat(b, false); });
        setCat(btn, !open);
      });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !dialog.hidden) closeDialog(dialog);
    });
  }

  /* ====================================================================
     图片淡入：懒加载的图解码完成会「啪」地出现，卡片切换时尤其明显。
     加载完再淡入，观感连续。已缓存的图（complete）直接标记，不做动画。
     ==================================================================== */
  function initImgFade(scope) {
    var sel = '.ifa-ergo__card-img, .ifa-exp__img, .ifa-home-about__brand-media img';
    (scope || document).querySelectorAll(sel).forEach(function (img) {
      if (img.dataset.ifaFade) return;
      img.dataset.ifaFade = '1';
      if (img.complete && img.naturalWidth > 0) {
        img.classList.add('is-loaded');
      } else {
        img.addEventListener('load', function () { img.classList.add('is-loaded'); }, { once: true });
        img.addEventListener('error', function () { img.classList.add('is-loaded'); }, { once: true });
      }
    });
  }

  /* ==================================================================== */
  function initAll(scope) {
    var el = scope || document;
    initImgFade(el);
    el.querySelectorAll('[data-ifa-home-nav]').forEach(initNav);
    el.querySelectorAll('[data-ifa-home-carousel]').forEach(initTrack);
    el.querySelectorAll('[data-ifa-ergo]').forEach(initErgo);
    el.querySelectorAll('[data-ifa-booth]').forEach(function (root) {
      initDialog(root);
      initBooth(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
